// Helpers for parsing YouTube URLs and querying the public oEmbed endpoint.

// Builds the oEmbed JSON endpoint for a video id. oEmbed is a public, no-auth endpoint
// that returns basic metadata (title, author, thumbnail) and is also handy as a lightweight
// availability check.
const buildOEmbedUrl = (videoId) =>
  `https://www.youtube.com/oembed?url=${encodeURIComponent(
    `https://www.youtube.com/watch?v=${videoId}`
  )}&format=json`;

/**
 * Extracts YouTube video ID from various YouTube URL formats
 * Supports: youtube.com, youtube-nocookie.com, youtu.be, embed URLs, etc.
 * @param {string} url - YouTube URL
 * @returns {string|null} - 11-character video ID or null if invalid
 */
export const extractYouTubeVideoId = (url) => {
  if (!url || typeof url !== 'string' || url.trim() === '') return null;

  // Comprehensive regex that handles multiple YouTube URL formats and validates video ID length
  const regExp =
    /(?:youtube(?:-nocookie)?\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regExp);

  return match ? match[1] : null;
};

/**
 * Checks whether a YouTube video is still publicly available via the oEmbed endpoint.
 * Fails open (returns true) on network/CORS errors so a flaky request doesn't falsely
 * flag a working video as unavailable.
 * @param {string} videoId - 11-character YouTube video ID
 * @returns {Promise<boolean>}
 */
export const isYouTubeVideoAvailable = async (videoId) => {
  if (!videoId) return true;

  try {
    const response = await fetch(buildOEmbedUrl(videoId));
    return response.ok;
  } catch {
    return true;
  }
};

/**
 * Fetches a YouTube video's title via the public oEmbed endpoint.
 * Returns null on any failure (network/CORS error, unavailable video) so callers
 * can silently skip auto-filling.
 * @param {string} videoId - 11-character YouTube video ID
 * @returns {Promise<string|null>}
 */
export const fetchYouTubeTitle = async (videoId) => {
  if (!videoId) return null;

  try {
    const response = await fetch(buildOEmbedUrl(videoId));

    if (!response.ok) return null;

    const { title } = await response.json();

    return title || null;
  } catch {
    return null;
  }
};
